import {
  useFeePeriodsByAccountQuery,
  useCopyBenefitsFromFeePeriodMutation,
} from '@transactions/graphql';
import {DateTime} from 'luxon';
import * as React from 'react';
import {useParams} from 'react-router-dom';
import {Button} from 'src/Button';
import {findDisplayPeriod} from 'src/CardBenefitsView/benefitUtils';
import {summarizeFeeYear} from 'src/CardBenefitsView/benefitWindows';
import {FeeYearSection} from 'src/CardBenefitsView/FeeYearSection';
import {useNowTick} from 'src/CardBenefitsView/useNowTick';
import {ScorecardTiles, YearBulletChart} from 'src/CardBenefitsView/YearScorecard';
import {DashedAddButton} from 'src/DashedAddButton';
import {useModals} from 'src/Modals';

export const CardBenefitsView = () => {
  const {id: accountId = ''} = useParams<{id: string}>();
  const {createFeePeriodModal} = useModals();
  // Window states and countdowns are clock-derived; tick so a page left
  // open across a day boundary stays honest
  useNowTick();

  const {data: feePeriodData, loading, refetch} =
    useFeePeriodsByAccountQuery({variables: {accountId}});

  const [copyBenefits] = useCopyBenefitsFromFeePeriodMutation();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading benefits...</span>
      </div>
    );
  }

  // Most recent fee period first. Window states depend on the clock, so
  // derive per render rather than memoizing on the query data alone.
  const now = DateTime.now();
  const feePeriods = [...(feePeriodData?.feePeriodsByAccount ?? [])].sort(
    (a, b) => b.startDate - a.startDate,
  );
  const yearStats = feePeriods.map((feePeriod) =>
    summarizeFeeYear(feePeriod, now),
  );
  const currentStats = yearStats.find((stats) => stats.isCurrent) ?? null;
  // With no current cycle, feature what findDisplayPeriod picks: the nearest
  // upcoming period (not the farthest-future prefill), else the most recent
  const expandedId =
    currentStats?.feePeriod._id ?? findDisplayPeriod(feePeriods)?._id ?? null;

  const handleRefetch = () => {
    void refetch();
  };

  const handleCreateFeePeriod = () => {
    createFeePeriodModal.openModal({
      accountId,
      excludeRawTransactionIds: feePeriods.map((fp) => fp.rawTransactionId),
      onSuccess: handleRefetch,
    });
  };

  const handleCopyFromPrevious = async (
    sourceFeePeriodId: string,
    targetFeePeriodId: string,
  ) => {
    try {
      await copyBenefits({
        variables: {sourceFeePeriodId, targetFeePeriodId},
      });
      void refetch();
    } catch {
      // Error is shown by Apollo
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
      {feePeriods.length === 0 ? (
        <div className="border-border rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground mb-4">No fee periods added yet</p>
          <p className="text-muted-foreground mb-4 text-sm">
            Add fee periods to track annual fees and their associated card
            benefits. Each fee period links to a fee charge transaction and
            contains the benefits for that period.
          </p>
          <Button variant="outline" onClick={handleCreateFeePeriod} type="button">
            Add your first fee period
          </Button>
        </div>
      ) : (
        <>
          {currentStats != null && currentStats.potential > 0 && (
            <ScorecardTiles stats={currentStats} />
          )}
          {yearStats.length >= 3 && <YearBulletChart years={yearStats} />}
          <DashedAddButton onClick={handleCreateFeePeriod}>
            Add Fee Period
          </DashedAddButton>
          {yearStats.map((stats, index) => (
            <FeeYearSection
              key={stats.feePeriod._id}
              stats={stats}
              defaultExpanded={stats.feePeriod._id === expandedId}
              // With the newest period first, the chronologically previous
              // period is the next entry in the list
              previousFeePeriodId={
                index < feePeriods.length - 1 &&
                feePeriods[index + 1].benefits.length > 0
                  ? feePeriods[index + 1]._id
                  : null
              }
              onUpdate={handleRefetch}
              onCopyFromPrevious={handleCopyFromPrevious}
            />
          ))}
        </>
      )}
    </div>
  );
};
